/**
 * The recycle bin.
 *
 * Everything deleted anywhere in the portal, in one list, with the days left before it
 * goes for good. One page rather than a per-page "show deleted" toggle: somebody
 * looking for something they removed by mistake usually remembers *that* they deleted
 * it, not which screen they were on at the time.
 *
 * Restoring is available to the office. Purging early is admin-only and irreversible —
 * the server enforces both, this only reflects it.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { MobileList } from '@/components/MobileList';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { Badge, Card, ErrorNotice, PageHeader, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, type RecycleItem } from '@/lib/api';
import { dateTime } from '@/lib/format';
import { useTableState, type SortOption } from '@/lib/table';

const SORTS: SortOption<RecycleItem>[] = [
  {
    key: 'deleted',
    labelKey: 'recycle.deletedAt',
    value: (i) => i.deletedAt,
    defaultDirection: 'desc',
  },
  { key: 'label', labelKey: 'recycle.item', value: (i) => i.label },
  { key: 'kind', labelKey: 'recycle.kind', value: (i) => i.kind },
  { key: 'daysLeft', labelKey: 'recycle.daysLeft', value: (i) => i.daysLeft },
];

const KIND_LABEL = {
  site: 'common.site',
  job: 'common.job',
  client: 'recycle.client',
  document: 'recycle.document',
  inventory: 'recycle.inventory',
} as const;

export function RecycleBinPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const isAdmin = me.data?.user?.role === 'admin';

  const bin = useQuery({ queryKey: ['recycle-bin'], queryFn: api.recycleBin });
  const [purging, setPurging] = useState<RecycleItem | null>(null);

  /*
   * Everything is invalidated, not just the bin.
   *
   * A restored site has to reappear in Sites, in the assignment pickers and in the job
   * form the moment it comes back — a list that still hides it until the next reload
   * looks exactly like the restore having failed.
   */
  const refresh = async () => {
    await queryClient.invalidateQueries();
  };

  const restore = useMutation({
    mutationFn: (item: RecycleItem) => api.restoreItem(item.kind, item.id),
    onSuccess: refresh,
  });

  const purge = useMutation({
    mutationFn: (item: RecycleItem) => api.purgeItem(item.kind, item.id),
    onSuccess: async () => {
      setPurging(null);
      await refresh();
    },
  });

  const table = useTableState<RecycleItem>({
    rows: bin.data?.items ?? [],
    search: (i) => [i.label, i.detail, i.deletedByName],
    sorts: SORTS,
    defaultSortKey: 'deleted',
    storageKey: 'recycle-bin',
  });

  const busy = restore.isPending || purge.isPending;

  return (
    <>
      <PageHeader
        title={t('recycle.title')}
        subtitle={
          bin.data
            ? t('recycle.subtitle', { count: bin.data.retentionDays })
            : t('recycle.subtitleShort')
        }
      />

      {bin.isLoading ? (
        <Spinner />
      ) : bin.error ? (
        <ErrorNotice error={bin.error} />
      ) : (
        <>
          <DataToolbar
            state={table}
            searchPlaceholder={t('recycle.searchPlaceholder')}
            countOne="recycle.countOne"
            countMany="recycle.countMany"
          />

          {restore.error ? <ErrorNotice error={restore.error} /> : null}

          {table.rows.length === 0 ? (
            <NoResults onClear={table.clear} />
          ) : (
            <Card padded={false}>
              <div className="lg:hidden p-2">
                  <MobileList
                    rows={table.rows}
                    keyOf={(i) => `${i.kind}-${i.id}`}
                    title={(i) => i.label ?? '\u2014'}
                    subtitle={(i) => i.detail ?? t(KIND_LABEL[i.kind])}
                    badges={(i) => (
                      <Badge tone={i.daysLeft <= 7 ? 'warning' : 'neutral'}>
                        {t('recycle.daysLeftValue', { count: i.daysLeft })}
                      </Badge>
                    )}
                    fields={[
                      { label: t('recycle.kind'), value: (i) => t(KIND_LABEL[i.kind]) },
                      {
                        label: t('recycle.deletedAt'),
                        value: (i) =>
                          `${dateTime(i.deletedAt)}${i.deletedByName ? ` \u00b7 ${i.deletedByName}` : ''}`,
                      },
                    ]}
                    action={(i) => (
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={busy}
                        onClick={() => restore.mutate(i)}
                      >
                        {t('recycle.restore')}
                      </button>
                    )}
                  />
                </div>

              <div className="table-scroll max-lg:hidden">
                <table className="table-wide">
                  <thead>
                    <tr>
                      <SortableTh state={table} sortKey="label">
                        {t('recycle.item')}
                      </SortableTh>
                      <SortableTh state={table} sortKey="kind">
                        {t('recycle.kind')}
                      </SortableTh>
                      <SortableTh state={table} sortKey="deleted">
                        {t('recycle.deletedAt')}
                      </SortableTh>
                      <SortableTh state={table} sortKey="daysLeft">
                        {t('recycle.daysLeft')}
                      </SortableTh>
                      <th className="th" />
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((item) => (
                      <tr key={`${item.kind}-${item.id}`} className="hover:bg-surface-alt">
                        <td className="td">
                          <span className="font-semibold">{item.label ?? '—'}</span>
                          {item.detail ? (
                            <div className="text-xs text-muted">{item.detail}</div>
                          ) : null}
                        </td>
                        <td className="td text-sm text-muted">{t(KIND_LABEL[item.kind])}</td>
                        <td className="td text-sm text-muted whitespace-nowrap">
                          {dateTime(item.deletedAt)}
                          {item.deletedByName ? (
                            <span className="block text-xs text-subtle">{item.deletedByName}</span>
                          ) : null}
                        </td>
                        <td className="td">
                          {/* Amber in the last week. The whole reason this column exists
                              is to say "decide about this one soon". */}
                          <Badge tone={item.daysLeft <= 7 ? 'warning' : 'neutral'}>
                            {t('recycle.daysLeftValue', { count: item.daysLeft })}
                          </Badge>
                        </td>
                        <td className="td text-end whitespace-nowrap">
                          <button
                            type="button"
                            className="td-action text-primary hover:underline"
                            disabled={busy}
                            onClick={() => restore.mutate(item)}
                          >
                            {t('recycle.restore')}
                          </button>
                          {isAdmin ? (
                            <button
                              type="button"
                              className="td-action text-danger hover:underline ms-3"
                              disabled={busy}
                              onClick={() => setPurging(item)}
                            >
                              {t('recycle.purge')}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Purging cannot be undone, so it asks — and names the thing, because "are you
          sure?" on its own is a question nobody reads. */}
      <Modal
        open={purging !== null}
        onClose={() => setPurging(null)}
        size="sm"
        title={t('recycle.purgeTitle')}
        description={purging ? t('recycle.purgeConfirm', { name: purging.label ?? '—' }) : ''}
        footer={
          <>
            <button
              type="button"
              className="btn-danger"
              disabled={purge.isPending}
              onClick={() => purging && purge.mutate(purging)}
            >
              {purge.isPending ? t('common.saving') : t('recycle.purge')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setPurging(null)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {purge.error ? <ErrorNotice error={purge.error} /> : null}
      </Modal>
    </>
  );
}
